import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  Target, 
  TrendingUp, 
  Users, 
  Clock, 
  Wallet,
  CheckCircle2, 
  AlertCircle,
  ArrowRight,
  Phone,
  MessageCircle,
  Lock,
  Lightbulb,
  BarChart3,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

// Analyze responses and generate scores
function analyzeResponses(data) {
  const scores = {
    acquisition: { score: 0, max: 100, label: 'Acquisition', icon: Users },
    organisation: { score: 0, max: 100, label: 'Organisation', icon: Clock },
    execution: { score: 0, max: 100, label: 'Exécution', icon: Target },
    ressources: { score: 0, max: 100, label: 'Ressources', icon: Wallet },
    potentiel: { score: 0, max: 100, label: 'Potentiel', icon: TrendingUp },
  };

  // ACQUISITION SCORING
  let acqScore = 50;
  if (data.canalPrincipal === 'nsp') acqScore -= 20;
  else if (data.canalPrincipal === 'reseau') acqScore += 10;
  else if (data.canalPrincipal === 'telephone' || data.canalPrincipal === 'terrain') acqScore += 20;
  
  if (data.tempsAcquisition >= 4) acqScore += 20;
  else if (data.tempsAcquisition >= 3) acqScore += 10;
  else if (data.tempsAcquisition <= 1) acqScore -= 15;
  
  if (data.lister30 === 'oui') acqScore += 15;
  else acqScore -= 10;
  
  scores.acquisition.score = Math.max(0, Math.min(100, acqScore));

  // ORGANISATION SCORING
  let orgScore = 50;
  if (data.suiviLeads === 'crm') orgScore += 25;
  else if (data.suiviLeads === 'tableur') orgScore += 10;
  else if (data.suiviLeads === 'nulle') orgScore -= 25;
  
  if (data.saisRelancer === 'oui') orgScore += 15;
  else orgScore -= 15;
  
  if (data.sourceLeads === 'oui') orgScore += 10;
  else orgScore -= 10;
  
  scores.organisation.score = Math.max(0, Math.min(100, orgScore));

  // EXECUTION SCORING
  let execScore = 50;
  if (data.suivrePlan >= 4) execScore += 20;
  else if (data.suivrePlan <= 2) execScore -= 15;
  
  if (data.inconfortable >= 4) execScore += 20;
  else if (data.inconfortable <= 2) execScore -= 15;
  
  if (data.execution >= 4) execScore += 15;
  else if (data.execution <= 2) execScore -= 10;
  
  if (data.repose === 'process') execScore += 10;
  else if (data.repose === 'moi') execScore -= 10;
  
  scores.execution.score = Math.max(0, Math.min(100, execScore));

  // RESSOURCES SCORING
  let resScore = 50;
  if (data.budget === '>6000') resScore += 25;
  else if (data.budget === '3000-6000') resScore += 15;
  else if (data.budget === '<1000') resScore -= 10;
  
  if (data.situationFinanciere === 'stable') resScore += 20;
  else if (data.situationFinanciere === 'tension') resScore -= 20;
  
  if (data.autreRevenu === 'oui') resScore += 15;
  else if (data.autreRevenu === 'non') resScore -= 10;
  
  scores.ressources.score = Math.max(0, Math.min(100, resScore));

  // POTENTIEL SCORING (based on ambition vs reality)
  let potScore = 60;
  const objectif = parseInt(data.objectif12mois) || 0;
  const heures = parseInt(data.tempsDisponible) || 0;
  
  if (objectif > 0 && heures >= 15) potScore += 20;
  else if (objectif > 0 && heures >= 10) potScore += 10;
  
  if (data.experienceEntrepreneuriale === 'oui') potScore += 15;
  else if (data.experienceEntrepreneuriale === 'projet') potScore += 5;
  
  if (data.situation === 'lancement' || data.situation === 'irregulier') potScore += 10;
  
  scores.potentiel.score = Math.max(0, Math.min(100, potScore));

  return scores;
}

// Generate personalized insights based on data
function generateInsights(data, scores) {
  const insights = [];
  const blindSpots = [];
  
  // Acquisition insights
  if (scores.acquisition.score < 50) {
    insights.push({
      type: 'warning',
      title: 'Acquisition à structurer',
      text: 'Ton flux de nouveaux propriétaires n\'est pas encore prévisible. C\'est le frein n°1 à la croissance.',
    });
    blindSpots.push('Combien de propriétaires dois-tu contacter pour en signer un ?');
  } else if (scores.acquisition.score >= 70) {
    insights.push({
      type: 'success',
      title: 'Bonne dynamique d\'acquisition',
      text: 'Tu as identifié un canal et tu es prêt à l\'activer. C\'est un bon point de départ.',
    });
  }

  // Organisation insights
  if (data.suiviLeads === 'nulle' || data.suiviLeads === 'whatsapp') {
    insights.push({
      type: 'warning',
      title: 'Suivi des leads inexistant',
      text: 'Sans suivi structuré, tu perds des opportunités. Un prospect non relancé est un prospect perdu.',
    });
    blindSpots.push('Combien de leads as-tu perdus par manque de relance ces 3 derniers mois ?');
  }

  // Execution insights
  if (data.repose === 'moi') {
    insights.push({
      type: 'warning',
      title: 'Activité trop dépendante de toi',
      text: 'Si tout repose sur toi, tu plafonnes. Impossible de scaler sans déléguer ou automatiser.',
    });
    blindSpots.push('Quelles tâches pourrais-tu déléguer dès maintenant pour libérer 5h/semaine ?');
  }

  // Time vs ambition
  const heures = parseInt(data.tempsDisponible) || 0;
  const objectif = parseInt(data.objectif12mois) || 0;
  if (heures < 10 && objectif > 5) {
    insights.push({
      type: 'warning',
      title: 'Écart temps / ambition',
      text: `${heures}h/semaine pour atteindre ${objectif} logements : le ratio est tendu. Il faudra optimiser ou revoir l'objectif.`,
    });
  }

  // Financial stability
  if (data.situationFinanciere === 'tension') {
    insights.push({
      type: 'warning',
      title: 'Pression financière',
      text: 'Lancer ou développer une activité sous pression financière multiplie les mauvaises décisions.',
    });
    blindSpots.push('Quel est ton runway actuel avant d\'avoir besoin de résultats ?');
  }

  // Positive signals
  if (data.inconfortable >= 4 && data.suivrePlan >= 4) {
    insights.push({
      type: 'success',
      title: 'Profil exécutant',
      text: 'Tu sembles capable de faire ce qui est difficile et de tenir un plan. C\'est rare et précieux.',
    });
  }

  // Experience
  if (data.experienceEntrepreneuriale === 'salarie') {
    blindSpots.push('Comment vas-tu gérer l\'incertitude et l\'absence de cadre ?');
  }

  return { insights, blindSpots };
}

// Calculate overall score
function calculateOverallScore(scores) {
  const values = Object.values(scores).map(s => s.score);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

// Get score color
function getScoreColor(score) {
  if (score >= 70) return 'text-emerald-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-500';
}

function getProgressColor(score) {
  if (score >= 70) return 'bg-emerald-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

// Questions mapping for human-readable format
const QUESTIONS_MAP = {
  // Pre-filter
  modele: { question: "Modèle principal", options: { conciergerie: "Conciergerie", souslocation: "Sous-location", mix: "Mix" }},
  logements: { question: "Nombre de logements aujourd'hui", options: { "0": "0", "1-2": "1 à 2", "3-4": "3 à 4", "5-9": "5 à 9", "10+": "10 ou plus" }},
  chiffreAffaires: { question: "Chiffre d'affaires mensuel", options: { "0": "0", "<3k": "Moins de 3 000 €", "3-10k": "3 000 à 10 000 €", "10k+": "Plus de 10 000 €" }},
  // Audit
  situation: { question: "Situation actuelle", options: { projet: "Projet / réflexion", lancement: "Lancement", irregulier: "En activité mais irrégulier", stagnation: "En stagnation" }},
  objectif12mois: { question: "Objectif réaliste à 12 mois (nombre de logements)" },
  tempsDisponible: { question: "Temps réel disponible par semaine (heures)" },
  suivrePlan: { question: "Je suis capable de suivre un plan sans me disperser (0-5)" },
  inconfortable: { question: "Je fais ce qui est inconfortable quand c'est nécessaire (0-5)" },
  repose: { question: "Aujourd'hui, l'activité repose principalement...", options: { moi: "Sur moi", prestataires: "Sur quelques prestataires", process: "Sur des process clairs" }},
  canalPrincipal: { question: "Canal principal d'acquisition", options: { reseau: "Réseau personnel", terrain: "Démarchage terrain", telephone: "Téléphone", annonces: "Annonces / plateformes", reseaux: "Réseaux sociaux", nsp: "Je ne sais pas encore" }},
  tempsAcquisition: { question: "Je peux consacrer 30 minutes par jour à l'acquisition (0-5)" },
  lister30: { question: "Je suis capable de lister 30 propriétaires ou cibles cette semaine", options: { oui: "Oui", non: "Non" }},
  suiviLeads: { question: "Aujourd'hui, les leads sont suivis...", options: { crm: "Dans un CRM", tableur: "Dans un tableur", whatsapp: "Dans WhatsApp / notes", nulle: "Nulle part / au feeling" }},
  saisRelancer: { question: "Sais-tu exactement qui relancer et quand ?", options: { oui: "Oui", non: "Non" }},
  sourceLeads: { question: "Sais-tu d'où viennent tes leads ?", options: { oui: "Oui", non: "Non" }},
  budget: { question: "Budget mobilisable pour structurer l'activité", options: { "<1000": "Moins de 1 000 €", "1000-3000": "1 000 à 3 000 €", "3000-6000": "3 000 à 6 000 €", ">6000": "Plus de 6 000 €" }},
  situationFinanciere: { question: "Situation financière actuelle", options: { stable: "Stable", variable: "Variable", tension: "En tension" }},
  autreRevenu: { question: "As-tu une autre source de revenus stable en parallèle ?", options: { oui: "Oui, revenus stables", partiel: "Oui, mais irréguliers", non: "Non, c'est ma seule activité" }},
  experienceEntrepreneuriale: { question: "As-tu déjà géré une activité ou un projet entrepreneurial ?", options: { oui: "Oui, j'ai déjà entrepris", salarie: "Non, j'ai toujours été salarié", projet: "J'ai des projets en parallèle" }},
  execution: { question: "Si on me donne un plan clair, je l'exécute avant de redemander (0-5)" },
  pourquoi: { question: "Pourquoi tu veux faire ça, vraiment ?" },
  abandonner: { question: "Qu'est-ce qui pourrait te faire abandonner ?" },
};

// Format questions and answers for webhook
function formatQuestionsAnswers(preFilterData, auditData) {
  const allData = { ...preFilterData, ...auditData };
  const questionsReponses = [];
  
  for (const [key, value] of Object.entries(allData)) {
    if (QUESTIONS_MAP[key]) {
      const questionInfo = QUESTIONS_MAP[key];
      let reponse = value;
      
      // Convert value to human-readable if options exist
      if (questionInfo.options && questionInfo.options[value]) {
        reponse = questionInfo.options[value];
      }
      
      questionsReponses.push({
        question: questionInfo.question,
        reponse: reponse
      });
    }
  }
  
  return questionsReponses;
}

// Send data to backend API (which forwards to webhook)
async function sendToWebhook(contactInfo, preFilterData, auditData, scores, contactChoice) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL|| '';
  const API_URL = `${BACKEND_URL}/api/diagnostic/submit`;
  
  console.log('=== ENVOI DIAGNOSTIC ===');
  console.log('Backend URL:', BACKEND_URL);
  console.log('API URL:', API_URL);
  
  const questionsReponses = formatQuestionsAnswers(preFilterData, auditData);
  
  const payload = {
    // Contact info
    prospect: {
      prenom: contactInfo.prenom,
      nom: contactInfo.nom,
      societe: contactInfo.societe || '',
      nombreLogements: contactInfo.nombreLogements || '',
      email: contactInfo.email,
      telephone: contactInfo.telephone,
    },
    // Contact preference
    preferenceContact: contactChoice === 'call' ? 'Appel téléphonique' : 'Message',
    // Scores
    scores: {
      global: Math.round(Object.values(scores).reduce((a, b) => a + b.score, 0) / 5),
      acquisition: scores.acquisition.score,
      organisation: scores.organisation.score,
      execution: scores.execution.score,
      ressources: scores.ressources.score,
      potentiel: scores.potentiel.score,
    },
    // Questions and answers
    questionsReponses: questionsReponses,
    // Timestamp
    dateSubmission: new Date().toISOString(),
  };

  console.log('Payload:', JSON.stringify(payload, null, 2));

  try {
    console.log('Envoi en cours...');
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('✅ Diagnostic envoyé avec succès:', result);
    return true;
  } catch (error) {
    console.error('❌ Erreur envoi diagnostic:', error);
    return false;
  }
}

export default function ResultsScreen({ preFilterData, auditData, contactInfo, onRestart }) {
  const [contactChoice, setContactChoice] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const scores = useMemo(() => analyzeResponses(auditData), [auditData]);
  const { insights, blindSpots } = useMemo(() => generateInsights(auditData, scores), [auditData, scores]);
  const overallScore = useMemo(() => calculateOverallScore(scores), [scores]);

  // Debug: vérifier que le composant est bien monté
  // Envoi automatique des résultats au montage (sans choix de contact)
  const [dataSent, setDataSent] = useState(false);

  useEffect(() => {
    console.log('=== ResultsScreen MONTÉ ===');
    console.log('contactInfo:', contactInfo);
    console.log('preFilterData:', preFilterData);
    console.log('auditData:', auditData);
    console.log('scores:', scores);
    console.log('REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);

    // Envoyer les données automatiquement au montage
    if (!dataSent) {
      console.log('=== ENVOI AUTOMATIQUE AU MONTAGE ===');
      sendToWebhook(contactInfo, preFilterData, auditData, scores, 'pending')
        .then((success) => {
          console.log('Envoi automatique résultat:', success ? 'OK' : 'ÉCHEC');
          setDataSent(true);
        })
        .catch((err) => {
          console.error('Erreur envoi automatique:', err);
        });
    }
  }, []);

  const handleContactSubmit = async (choice) => {
    console.log('=== handleContactSubmit appelé avec:', choice, '===');
    setSending(true);
    
    // Send to webhook
    const success = await sendToWebhook(contactInfo, preFilterData, auditData, scores, choice);
    
    setSending(false);
    setContactChoice(choice);
    setSubmitted(true);
    
    if (success) {
      if (choice === 'call') {
        toast.success('Parfait ! Tu seras contacté sous 24h pour un échange.');
      } else {
        toast.success('Bien noté. Tu recevras un message sous 24h.');
      }
    } else {
      // Even if webhook fails, show success to user (data is logged)
      if (choice === 'call') {
        toast.success('Parfait ! Tu seras contacté sous 24h pour un échange.');
      } else {
        toast.success('Bien noté. Tu recevras un message sous 24h.');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="w-full border-b border-border bg-background">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-foreground">Ton diagnostic</span>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 px-6 py-10">
        <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">
          
          {/* Personalized Header */}
          <div className="text-center space-y-3">
            <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
              {contactInfo?.prenom}, voici ta lecture
            </h1>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Basé sur tes réponses, voici une première analyse de ta situation. 
              Ce n&apos;est pas un jugement, c&apos;est un point de départ.
            </p>
          </div>

          {/* Overall Score Card */}
          <Card className="border-border shadow-lg overflow-hidden">
            <div className="h-1.5 bg-primary" />
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-muted"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${overallScore * 3.52} 352`}
                      className={getScoreColor(overallScore)}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-3xl font-bold ${getScoreColor(overallScore)}`}>
                      {overallScore}
                    </span>
                  </div>
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    Score de maturité
                  </h2>
                  <p className="text-muted-foreground text-sm max-w-xs">
                    {overallScore >= 70 
                      ? 'Tu as des bases solides. L\'enjeu est maintenant de structurer pour accélérer.'
                      : overallScore >= 50
                      ? 'Des fondations existent mais plusieurs axes nécessitent une attention immédiate.'
                      : 'Plusieurs points de vigilance identifiés. Mieux vaut les adresser avant d\'accélérer.'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dimension Scores */}
          <Card className="border-border shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Tes 5 dimensions clés
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {Object.entries(scores).map(([key, data]) => {
                const Icon = data.icon;
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{data.label}</span>
                      </div>
                      <span className={`text-sm font-semibold ${getScoreColor(data.score)}`}>
                        {data.score}/100
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${getProgressColor(data.score)}`}
                        style={{ width: `${data.score}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Key Insights */}
          <Card className="border-border shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                Ce que nous avons identifié
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {insights.slice(0, 3).map((insight, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-lg border ${
                    insight.type === 'success' 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {insight.type === 'success' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <h4 className={`font-medium ${
                        insight.type === 'success' ? 'text-emerald-900' : 'text-amber-900'
                      }`}>
                        {insight.title}
                      </h4>
                      <p className={`text-sm mt-1 ${
                        insight.type === 'success' ? 'text-emerald-700' : 'text-amber-700'
                      }`}>
                        {insight.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Blind Spots - The Hook */}
          {blindSpots.length > 0 && (
            <Card className="border-border shadow-md bg-gradient-to-br from-slate-50 to-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  Les questions qu&apos;on aimerait creuser avec toi
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Ces points méritent une vraie conversation pour y voir plus clair.
                </p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {blindSpots.slice(0, 3).map((question, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{question}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* CTA Section */}
          <Card className="border-2 border-primary/20 shadow-lg overflow-hidden">
            <div className="h-1 bg-primary" />
            <CardContent className="p-6 sm:p-8">
              {!submitted ? (
                <>
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      On en parle ?
                    </h3>
                    <p className="text-muted-foreground">
                      Un échange de 15 minutes pour reprendre ton diagnostic, 
                      répondre à tes questions et t&apos;aider à y voir plus clair.
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Pas de pitch, pas de vente. Juste un point utile.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button 
                      size="lg" 
                      className="w-full gap-2"
                      onClick={() => handleContactSubmit('call')}
                      disabled={sending}
                    >
                      {sending ? (
                        <span className="animate-pulse">Envoi en cours...</span>
                      ) : (
                        <>
                          <Phone className="w-5 h-5" />
                          Oui, on s&apos;appelle
                        </>
                      )}
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => handleContactSubmit('message')}
                      disabled={sending}
                    >
                      {sending ? (
                        <span className="animate-pulse">Envoi en cours...</span>
                      ) : (
                        <>
                          <MessageCircle className="w-5 h-5" />
                          Plutôt par message
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-center text-muted-foreground mt-4">
                    Contact sous 24h · Aucun engagement · Échange confidentiel
                  </p>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    C&apos;est noté, {contactInfo?.prenom}
                  </h3>
                  <p className="text-muted-foreground">
                    {contactChoice === 'call' 
                      ? 'Tu seras contacté sous 24h pour convenir d\'un créneau.'
                      : 'Tu recevras un message sous 24h.'
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Final Note */}
          <div className="border-l-2 border-primary pl-4 py-2">
            <p className="text-sm text-muted-foreground italic">
              Ce diagnostic est une première lecture, pas une vérité absolue. 
              L&apos;important, c&apos;est ce que tu en fais maintenant.
            </p>
          </div>

          {/* Restart */}
          <div className="text-center pt-4">
            <Button variant="ghost" onClick={onRestart} className="text-muted-foreground">
              Recommencer le diagnostic
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
